import { useRefinementList } from 'react-instantsearch';
import AccordionItem from '@monofront/design-system/components/Accordion';
import Label from '@monofront/design-system/components/Form/Label';
import { RefinementListConnectorParams } from 'instantsearch.js/es/connectors/refinement-list/connectRefinementList';
import { ALGOLIA_FACET_VALUES, ALGOLIA_GRADE_FACET_REGEX, ALGOLIA_INSIGHTS_EVENT_NAME } from '@monofront/business/constants';

type RefinementListProps = {
  attribute: RefinementListConnectorParams['attribute'];
  title: string;
};

// Temporary component until responsive filters will be done
const GradeFilter: React.FC<RefinementListProps> = ({ attribute, title }) => {
  const { items: availableRefinements, refine, sendEvent } = useRefinementList({ attribute });

  // Keep only letter scores (A to F and special A+)
  const usableItems = availableRefinements.filter(
    ({ label }) => label.match(ALGOLIA_GRADE_FACET_REGEX),
  );

  // Sort alphabetically, with A+ first
  usableItems.sort((a, b) => {
    if (a.label.toLowerCase() === "a-plus") return -1;
    if (b.label.toLowerCase() === "a-plus") return 1;

    return a.label.localeCompare(b.label);
  });

  return (
    <AccordionItem title={title}>
      <ul>
        {usableItems.map((item, index) => {
          const idName = `${item.label}-${index}`;

          return (
            <li key={item.label}>
              <span
                onClick={(event) => {
                  event.preventDefault();
                  refine(item.value);

                  if (item.value.length > 0) {
                    // Not in the doc™️, but `click` forwards a `clickedFilters` event to insights
                    // See instantsearch.js/src/lib/utils/createSendEventForFacet.ts
                    sendEvent(
                      'click',
                      item.value,
                      ALGOLIA_INSIGHTS_EVENT_NAME.FILTER_CLICKED,
                    );
                  }
                }}
              >
                <div
                  className="checkbox"
                  role="checkbox"
                  aria-checked={item.isRefined}
                  aria-labelledby={idName}
                ></div>
                <Label id={idName}>{ALGOLIA_FACET_VALUES[item.label] ? ALGOLIA_FACET_VALUES[item.label] : item.label}</Label>
              </span>
            </li>
          );
        })}
      </ul>
      <style jsx>{`
        ul {
          padding: var(--space-4) var(--space-4);
          max-height: 300px;
          overflow-y: unset;
          border-bottom: var(--border);
          background-color: var(--clr-primary-6);
        }

        ul:empty {
          display: none;
        }

        ul > li:not(:last-child) {
          margin-bottom: var(--space-2);
        }

        span {
          display: flex;
          cursor: pointer;
        }

        span > :global(label) {
          cursor: pointer;
        }

        span > :global(input) {
          margin-right: var(--space-4);
        }

        .checkbox {
          border: 2px solid var(--clr-checkbox-border);
          border-radius: 3px;
          width: 20px;
          min-width: 20px;
          height: 20px;
          background-color: var(--white);
          color: var(--white);
          margin-right: var(--space-4);
        }

        .checkbox:after {
          content: ' ';
          width: 20px;
          height: 20px;
          display: inline-block;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
        }

        .checkbox[aria-checked='true']:after {
          transform: scale(0.7);
          background-color: var(--clr-primary-1);
          display: flex;
          width: 100%;
          height: 100%;
          border-radius: 2px;
        }

        @media (max-width: 800px) {
          ul {
            background-color: var(--clr-primary-5);
          }
        }
      `}</style>
    </AccordionItem>
  );
};

export default GradeFilter;
